import { useEffect, useRef } from 'react'
import { useQuery } from '@rocicorp/zero/react'
import { queries } from '../zero/queries.ts'

/**
 * Evaluated once when the module first loads — i.e. per page load, not per
 * mount. The ordering flow unmounts this component on every step change, so a
 * mount-scoped cutoff would wipe the log on each button press.
 */
const OPENED_AT = Date.now()

function formatTime(ms: number) {
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * Live ticker of orders, in the scrolling terminal style of the old kiosk.
 *
 * This is the clearest demo of Zero's reactivity: an order tapped in one window
 * (or arriving via the ESP32 button-box) appears here in every other open
 * window instantly. The feed uses a sanitized projection that contains no
 * payment linkage or paid/unpaid status.
 */
export function ActivityFeed() {
  // The log is "what has happened since this page was opened", so a reload
  // starts it empty instead of replaying history — and the cutoff is part of
  // the query, so old rows never reach the client.
  const [orders] = useQuery(queries.recentOrders({ since: OPENED_AT }))
  const boxRef = useRef<HTMLDivElement>(null)

  // Newest entry is at the top, so that is where the box should sit.
  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = 0
  }, [orders.length])

  return (
    <div className="activity-log" ref={boxRef}>
      {orders.length === 0 ? (
        <span className="activity-log__empty">No activity yet</span>
      ) : (
        orders.map((o) => (
          <div className="activity-log__entry" key={o.id}>
            <span className="activity-log__time">[{formatTime(o.purchasedAt)}]</span>
            <span className="activity-log__message">
              {' '}
              {o.memberName} ordered {o.drinkDescription}
              {o.guestName ? ` for ${o.guestName}` : ''}
            </span>
          </div>
        ))
      )}
    </div>
  )
}
